// monitor/index.js
// 백엔드 API 호출 텔레메트리를 error-monitoring 서버로 전송하는 경량 SDK.
//
// 이 서버는 "HTTP 요청 모니터링" 전용이며, 이벤트 스키마가 고정되어 있다.
// (2026-07-31 서버 스펙 역추적으로 확인)
//
//   POST <endpoint>
//   Authorization: Bearer <ingest key>
//   {
//     "events": [
//       {
//         "method":      string,        // 필수. GET/POST/...
//         "route":       string,        // 필수. 백엔드 baseURL 제거된 path
//         "status":      number(int>=100), // 필수. HTTP 상태코드
//         "duration_ms": number,        // 필수. 요청 소요(ms)
//         "occurred_at": string(ISO datetime), // 필수
//         "release":     string         // 선택
//       }
//     ]
//   }
//
// - 성공 시 202 { ok:true, accepted, issues }. 서버가 4xx/5xx status를 '이슈'로 자동 판별한다.
// - 전송은 fetch(keepalive)로만 수행한다(앱 axios 미사용 → 자기참조 루프 없음).
// - 키/엔드포인트가 없으면 전체가 no-op으로 동작한다(로컬 개발 안전).

// env 값에 섞여 들어온 공백/개행 제거
// (Amplify 콘솔 등에 긴 URL/키를 붙여넣을 때 줄바꿈 공백이 끼는 사고 방지)
function cleanEnv(value) {
  return (value || "").replace(/\s+/g, "");
}

const ENDPOINT = cleanEnv(process.env.REACT_APP_MONITOR_ENDPOINT);
const INGEST_KEY = cleanEnv(process.env.REACT_APP_MONITOR_INGEST_KEY);
const RELEASE = (process.env.REACT_APP_MONITOR_RELEASE || "").trim();

const ENABLED = Boolean(ENDPOINT && INGEST_KEY);

// 무응답(네트워크 오류 등)으로 status가 없을 때 사용할 합성 코드.
// 서버 스키마상 status는 정수 >= 100 필수이므로 599(네트워크 계열)로 표기한다.
const NETWORK_ERROR_STATUS = 599;

const MAX_BATCH = 20; // 한 번에 보낼 최대 이벤트 수
const MAX_QUEUE = 100; // 큐 상한(초과분은 드롭)
const FLUSH_INTERVAL_MS = 4000;

let queue = [];
let flushTimer = null;

function nowISO() {
  try {
    return new Date().toISOString();
  } catch {
    return null;
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!ENABLED || queue.length === 0) return;

  // 큐를 전부 즉시 전송한다. 리다이렉트(window.location.href)나 페이지 언로드
  // 직전에 호출돼도 남은 배치를 4초 뒤로 미루지 않아 유실을 막는다.
  // send 는 fire-and-forget(keepalive)이라 언로드 중에도 전송이 보장된다(64KB 이하).
  while (queue.length > 0) {
    const batch = queue.slice(0, MAX_BATCH);
    queue = queue.slice(MAX_BATCH);
    send(batch);
  }
}

// 실제 전송 (fire-and-forget). 실패해도 앱에 영향을 주지 않는다.
function send(events) {
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${INGEST_KEY}`,
      },
      body: JSON.stringify({ events }),
      keepalive: true, // 페이지 이탈 시에도 전송 보장(64KB 이하)
      credentials: "omit",
      mode: "cors",
    }).catch(() => {});
  } catch {
    // ignore
  }
}

function enqueue(event) {
  if (!ENABLED) return;
  if (queue.length >= MAX_QUEUE) return; // 과부하 시 드롭
  queue.push(event);
  if (queue.length >= MAX_BATCH) flush();
  else scheduleFlush();
}

// ── 공개 API ────────────────────────────────────────────────

// 백엔드 API 호출 1건을 서버 스키마에 맞춰 수집한다.
// 인자: { method, route, status, duration_ms }
function captureHttp({ method, route, status, duration_ms } = {}) {
  if (!ENABLED) return;

  const occurred_at = nowISO();
  if (!occurred_at) return; // 필수 필드 없으면 전송하지 않음

  const normalizedStatus =
    typeof status === "number" && status >= 100
      ? Math.round(status)
      : NETWORK_ERROR_STATUS;

  const normalizedDuration =
    typeof duration_ms === "number" && duration_ms >= 0
      ? Math.round(duration_ms)
      : 0;

  const event = {
    method: String(method || "GET").toUpperCase(),
    route: String(route || "/"),
    status: normalizedStatus,
    duration_ms: normalizedDuration,
    occurred_at,
  };
  if (RELEASE) event.release = RELEASE;

  enqueue(event);
}

// 앱 진입점에서 1회 호출: 페이지 이탈 시 잔여 큐를 마저 전송한다.
function init() {
  if (!ENABLED || typeof window === "undefined") return;

  const flushIfHidden = () => {
    if (document.visibilityState === "hidden") flush();
  };
  window.addEventListener("visibilitychange", flushIfHidden);
  window.addEventListener("pagehide", flush);
}

export const monitor = {
  enabled: ENABLED,
  init,
  captureHttp,
  flush, // 하드 리다이렉트(세션 만료 등) 직전 큐를 즉시 비워 유실 방지
};

export default monitor;
